import React, { useMemo, useState } from 'react';
import { CalendarClock, Mail, Search, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

const daysOpen = (dateStr) => {
  try { return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000)); }
  catch { return 0; }
};
const bucketKey = (d) => (d <= 30 ? '0-30' : d <= 60 ? '31-60' : d <= 90 ? '61-90' : '90+');
const BUCKETS = [
  { key: '0-30', label: '0-30 dgn', color: 'var(--cs-green)', badge: 'green' },
  { key: '31-60', label: '31-60 dgn', color: 'var(--cs-accent)', badge: 'blue' },
  { key: '61-90', label: '61-90 dgn', color: '#f59e0b', badge: 'amber' },
  { key: '90+', label: '90+ dgn', color: 'var(--cs-red)', badge: 'red' },
];

export default function Debiteuren() {
  const { t } = useT();
  const { bonnen, klanten, gegevens } = useData();
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null); // debiteur object voor modal
  const [sending, setSending] = useState('');

  // Alle openstaande facturen (openstaand > 0)
  const openFacturen = useMemo(() => bonnen
    .filter(b => (b.openstaand || 0) > 0.005)
    .map(b => {
      const d = daysOpen(b.datum);
      return { ...b, dagenOpen: d, bucket: bucketKey(d), klant: klanten.find(k => k.id === b.klantId) || null };
    }), [bonnen, klanten]);

  // Groepeer per debiteur (klant)
  const debiteuren = useMemo(() => {
    const map = {};
    openFacturen.forEach(f => {
      const id = f.klantId || 'onbekend';
      if (!map[id]) map[id] = { klantId: id, klant: f.klant, naam: f.klant?.naam || t('Onbekende klant', 'Onbekende klant'), email: f.klant?.email || '', facturen: [], totaal: 0, oudste: 0 };
      map[id].facturen.push(f);
      map[id].totaal += (f.openstaand || 0);
      map[id].oudste = Math.max(map[id].oudste, f.dagenOpen);
    });
    return Object.values(map).map(d => ({ ...d, bucket: bucketKey(d.oudste) }))
      .sort((a, b) => b.oudste - a.oudste);
  }, [openFacturen, t]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return debiteuren.filter(d => !f || d.naam.toLowerCase().includes(f) || (d.email || '').toLowerCase().includes(f));
  }, [debiteuren, filter]);

  const totaalOpen = openFacturen.reduce((s, f) => s + (f.openstaand || 0), 0);
  const teLaat = openFacturen.filter(f => f.dagenOpen > 30).reduce((s, f) => s + (f.openstaand || 0), 0);
  const gemDagen = openFacturen.length ? Math.round(openFacturen.reduce((s, f) => s + f.dagenOpen, 0) / openFacturen.length) : 0;

  const agingTotals = useMemo(() => {
    const init = { '0-30': { aantal: 0, bedrag: 0 }, '31-60': { aantal: 0, bedrag: 0 }, '61-90': { aantal: 0, bedrag: 0 }, '90+': { aantal: 0, bedrag: 0 } };
    openFacturen.forEach(f => { init[f.bucket].aantal += 1; init[f.bucket].bedrag += (f.openstaand || 0); });
    return init;
  }, [openFacturen]);

  const buildReminder = (deb) => {
    const garageNaam = gegevens?.naam || 'MonteurCentraal';
    const regels = deb.facturen
      .sort((a, b) => (a.datum || '').localeCompare(b.datum || ''))
      .map(f => `- ${f.type || 'Factuur'} ${f.nummer || ''} d.d. ${f.datum ? new Date(f.datum).toLocaleDateString('nl-NL') : ''} — openstaand ${eur(f.openstaand)} (${f.dagenOpen} ${t('dagen open', 'dagen open')})`)
      .join('\n');
    const onderwerp = `${t('Betalingsherinnering', 'Betalingsherinnering')} — ${garageNaam}`;
    const body = `${t('Beste', 'Beste')} ${deb.naam},

${t('Volgens onze administratie staat het volgende bedrag nog open:', 'Volgens onze administratie staat het volgende bedrag nog open:')}

${regels}

${t('Totaal openstaand', 'Totaal openstaand')}: ${eur(deb.totaal)}

${t('Wij verzoeken u vriendelijk het openstaande bedrag te voldoen. Heeft u inmiddels betaald? Dan kunt u deze herinnering als niet verzonden beschouwen.', 'Wij verzoeken u vriendelijk het openstaande bedrag te voldoen. Heeft u inmiddels betaald? Dan kunt u deze herinnering als niet verzonden beschouwen.')}

${t('Met vriendelijke groet', 'Met vriendelijke groet')},
${garageNaam}${gegevens?.telefoon ? `\n${gegevens.telefoon}` : ''}${gegevens?.email ? `\n${gegevens.email}` : ''}`;
    return { onderwerp, body };
  };

  const stuurHerinnering = async (deb) => {
    if (!deb.email) { toast.error(t('Deze klant heeft geen e-mailadres', 'Deze klant heeft geen e-mailadres')); return; }
    const { onderwerp, body } = buildReminder(deb);
    setSending(deb.klantId);
    try {
      await carsysApi.sendEmail({ aan: deb.email, onderwerp, body, type: 'herinnering' });
      toast.success(`${t('Herinnering verstuurd naar', 'Herinnering verstuurd naar')} ${deb.naam}`);
    } catch (e) {
      toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setSending(''); }
  };

  return (
    <div data-testid="debiteuren-page">
      <div className="cs-page-header">
        <div><h2>{t('Debiteurenbeheer', 'Debiteurenbeheer')}</h2><p>{t('Openstaande facturen, ouderdomsanalyse (aging) en betalingsherinneringen', 'Openstaande facturen, ouderdomsanalyse (aging) en betalingsherinneringen')}</p></div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="kpi-deb-totaal"><div className="label">{t('Totaal openstaand', 'Totaal openstaand')}</div><div className="value" style={{ color: 'var(--cs-red)' }}>{eur(totaalOpen)}</div></div>
        <div className="cs-kpi" data-testid="kpi-deb-telaat"><div className="label">{t('Te laat (> 30 dagen)', 'Te laat (> 30 dagen)')}</div><div className="value" style={{ color: '#f59e0b' }}>{eur(teLaat)}</div></div>
        <div className="cs-kpi" data-testid="kpi-deb-gemiddeld"><div className="label">{t('Gem. dagen open', 'Gem. dagen open')}</div><div className="value">{gemDagen} <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--cs-text-muted)' }}>{t('dagen', 'dagen')}</span></div></div>
      </div>

      <div className="cs-card padded" style={{ marginBottom: 14 }}>
        <h3 className="cs-card-title"><CalendarClock size={16} /> {t('Ouderdomsanalyse (aging)', 'Ouderdomsanalyse (aging)')}</h3>
        <div className="cs-four-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {BUCKETS.map(b => (
            <div key={b.key} data-testid={`aging-${b.key}`} style={{ padding: 12, borderRadius: 8, background: 'var(--cs-card-alt)', borderLeft: `3px solid ${b.color}` }}>
              <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{t(b.label, b.label)}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: b.color }}>{eur(agingTotals[b.key].bedrag)}</div>
              <div style={{ fontSize: 11, color: 'var(--cs-text-muted)' }}>{agingTotals[b.key].aantal} {t('facturen', 'facturen')}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" data-testid="deb-search" style={{ paddingLeft: 30 }} placeholder={t('Zoeken op klant of e-mail...', 'Zoeken op klant of e-mail...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr>
            <th>{t('Klant', 'Klant')}</th>
            <th>{t('E-mail', 'E-mail')}</th>
            <th style={{ textAlign: 'center' }}>{t('Open facturen', 'Open facturen')}</th>
            <th style={{ textAlign: 'center' }}>{t('Oudste', 'Oudste')}</th>
            <th style={{ textAlign: 'right' }}>{t('Totaal openstaand', 'Totaal openstaand')}</th>
            <th style={{ textAlign: 'right' }}>{t('Actie', 'Actie')}</th>
          </tr></thead>
          <tbody>
            {filtered.map(d => {
              const badge = BUCKETS.find(b => b.key === d.bucket)?.badge || 'gray';
              return (
                <tr key={d.klantId} data-testid={`deb-row-${d.klantId}`} style={{ cursor: 'pointer' }} onClick={() => setDetail(d)} title={t('Klik voor details', 'Klik voor details')}>
                  <td style={{ fontWeight: 600 }}>{d.naam}</td>
                  <td style={{ color: 'var(--cs-text-muted)', fontSize: 12 }}>{d.email || <span style={{ color: 'var(--cs-red)' }}>{t('geen e-mail', 'geen e-mail')}</span>}</td>
                  <td style={{ textAlign: 'center' }}>{d.facturen.length}</td>
                  <td style={{ textAlign: 'center' }}><span className={`cs-badge ${badge}`}>{d.oudste} {t('dgn', 'dgn')}</span></td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--cs-red)' }}>{eur(d.totaal)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="cs-btn" data-testid={`deb-reminder-${d.klantId}`} disabled={!d.email || sending === d.klantId} onClick={(e) => { e.stopPropagation(); stuurHerinnering(d); }} title={d.email ? t('Betalingsherinnering mailen', 'Betalingsherinnering mailen') : t('Geen e-mailadres bekend', 'Geen e-mailadres bekend')}>
                      <Mail size={13} /> {sending === d.klantId ? t('Bezig...', 'Bezig...') : t('Herinnering', 'Herinnering')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="cs-empty">{t('Geen openstaande facturen. Alle debiteuren hebben betaald! 🎉', 'Geen openstaande facturen. Alle debiteuren hebben betaald!')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="cs-modal-overlay" onClick={() => setDetail(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ width: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <h3 style={{ marginTop: 0 }}>{detail.naam}</h3>
              <button className="cs-btn ghost" style={{ padding: 4 }} onClick={() => setDetail(null)}><X size={16} /></button>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
              {detail.email || t('geen e-mailadres', 'geen e-mailadres')} · {detail.facturen.length} {t('open facturen', 'open facturen')} · {t('totaal', 'totaal')} <b style={{ color: 'var(--cs-red)' }}>{eur(detail.totaal)}</b>
            </div>
            <table className="cs-table">
              <thead><tr><th>{t('Nummer', 'Nummer')}</th><th>{t('Datum', 'Datum')}</th><th style={{ textAlign: 'center' }}>{t('Dagen', 'Dagen')}</th><th style={{ textAlign: 'right' }}>{t('Openstaand', 'Openstaand')}</th></tr></thead>
              <tbody>
                {detail.facturen.sort((a, b) => b.dagenOpen - a.dagenOpen).map(f => {
                  const badge = BUCKETS.find(b => b.key === f.bucket)?.badge || 'gray';
                  return (
                    <tr key={f.id}>
                      <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{f.nummer}</td>
                      <td>{f.datum ? new Date(f.datum).toLocaleDateString('nl-NL') : '-'}</td>
                      <td style={{ textAlign: 'center' }}><span className={`cs-badge ${badge}`}>{f.dagenOpen}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--cs-red)' }}>{eur(f.openstaand)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setDetail(null)}>{t('Sluiten', 'Sluiten')}</button>
              <button className="cs-btn primary" data-testid="deb-detail-reminder" disabled={!detail.email || sending === detail.klantId} onClick={() => stuurHerinnering(detail)}>
                <Mail size={13} /> {sending === detail.klantId ? t('Bezig...', 'Bezig...') : t('Herinnering sturen', 'Herinnering sturen')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 560px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); max-height: 88vh; overflow: auto; }
      `}</style>
    </div>
  );
}
